import { BehaviorSubject, Observable } from 'rxjs';

import { ILogger } from '@shure/shared/angular/utils/logging';

export class ObservableRepository<TEntity extends { id: string }> {
	private readonly logger: ILogger;
	private repository: Map<string, BehaviorSubject<TEntity>> = new Map();
	private defaultEntity: TEntity;

	/**
	 *
	 * @param logger logger class
	 * @param defaultEntity a template for new default records in the repository
	 */
	constructor(logger: ILogger, defaultEntity: TEntity) {
		this.logger = logger.createScopedLogger('EntityRepository');
		this.defaultEntity = defaultEntity;
	}

	/**
	 *
	 * @param key Provide an Observable entry for the given record. If not found a default template is given.
	 * @returns An observable reference object.
	 */
	public get$(key: string): Observable<TEntity> {
		let repoEntity = this.repository.get(key);
		if (!repoEntity) {
			const insertion = <TEntity>{ ...this.defaultEntity };
			insertion.id = key;
			repoEntity = new BehaviorSubject<TEntity>(insertion);
			this.repository.set(key, repoEntity);
		}
		return repoEntity.asObservable();
	}

	/**
	 *
	 * @param key record id
	 * @param newValue TEntity to insert
	 * @returns
	 */

	public set$(key: string, newValue: TEntity): Observable<TEntity> {
		let entity = this.repository.get(key);
		if (!entity) {
			entity = new BehaviorSubject<TEntity>(newValue);
			this.repository.set(key, entity);
		} else {
			this.repository.set(key, entity);
			entity.next(newValue);
		}
		return entity.asObservable();
	}

	/**
	 * Update function to update value in a given record.
	 * @param key lookup value for repository entry
	 * @param updateFN mapper method to update found record
	 * @returns returns the updated record
	 */
	public update(key: string, updateFN: (currentValue: TEntity) => void): BehaviorSubject<TEntity> | undefined {
		this.logger.trace('update entity', key);
		const entity = this.repository.get(key);
		if (entity) {
			const currentValue = entity.getValue();
			updateFN(currentValue);
			entity.next(currentValue);
			return entity;
		}
		return undefined;
	}

	public remove(key: string): void {
		this.repository.delete(key);
	}

	public getAllEntities(): TEntity[] {
		const map = [...this.repository.values()];
		const entities = map.map((m) => m.value);
		return entities;
	}
}
